import { PropsWithChildren } from 'lib-shared-types-utils';
import isEqual from 'lodash/isEqual';
import { HTMLAttributes, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePrevious } from 'react-use';

export type ShadowDomProps = PropsWithChildren & {
  /**
   * The encapsulation mode for the shadow DOM tree.
   *
   * - `open`: The elements in the shadow root are accessible from the outside.
   * - `closed`: The elements are hidden from the outside.
   */
  mode?: 'open' | 'closed';
  /**
   * A boolean that, when set to true, specifies behavior that mitigates custom
   * element issues around focusability. When a non-focusable part of the shadow
   * DOM is clicked, the first focusable part is given focus, and the shadow
   * host is given any available :focus styling. Defaults to false.
   */
  delegatesFocus?: boolean;
  /**
   * The css stylesheets to include in the shadow DOM tree.
   *
   * Each goes into a `style` tag that becomes as sibling to the projected
   * children.
   */
  stylesheets?: readonly string[];
  /** Whether to reset all inheritable styles. Defaults to true. */
  resetStyles?: boolean;
  /**
   * TabIndex for the shadow host, useful for disabling focusability and setting
   * tab order.
   */
  tabIndex?: string;
} & HTMLAttributes<HTMLDivElement>;

/** Represents a components that encapsulates the children in a shadow root. */
export const ShadowDom = ({
  mode = 'closed',
  resetStyles = true,
  delegatesFocus = false,
  stylesheets,
  tabIndex,
  children,
  ...divProps
}: ShadowDomProps): JSX.Element => {
  const [shadowRoot, setShadowRoot] = useState<ShadowRoot>();
  const previousStylesheets = usePrevious(stylesheets);

  const handleShadowHostRef = useCallback(
    (shadowHost: HTMLDivElement | null) => {
      if (shadowHost) {
        const newShadowRoot =
          shadowRoot ??
          shadowHost.attachShadow({
            delegatesFocus,
            mode,
          });

        if (tabIndex) {
          shadowHost.setAttribute('tabindex', tabIndex);
        }

        if (!isEqual(previousStylesheets, stylesheets)) {
          newShadowRoot
            .querySelectorAll('style')
            .forEach((style) => style.remove());

          if (resetStyles) {
            const style = document.createElement('style');
            style.textContent = ':host { all: initial; }';
            newShadowRoot.appendChild(style);
          }

          if (stylesheets) {
            stylesheets.forEach((stylesheet) => {
              const style = document.createElement('style');
              style.textContent = stylesheet;
              newShadowRoot.appendChild(style);
            });
          }
        }

        if (newShadowRoot !== shadowRoot) {
          setShadowRoot(newShadowRoot);
        }
      }
    },
    [
      delegatesFocus,
      mode,
      previousStylesheets,
      resetStyles,
      shadowRoot,
      stylesheets,
      tabIndex,
    ],
  );

  return (
    <div ref={handleShadowHostRef} {...divProps}>
      {shadowRoot
        ? createPortal(children, shadowRoot as unknown as Element)
        : children}
    </div>
  );
};
